import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ArrowBack, ChevronLeft, ChevronRight, Search, Visibility } from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { teacherModuleAPI } from '../../services/api';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function formatSchedules(schedules = []) {
  // e.g. "Mon, Wed | 04:00 PM - 05:30 PM"
  if (!Array.isArray(schedules) || schedules.length === 0) return 'Schedule not set';
  const days = Array.from(new Set(schedules.map((s) => DOW[Number(s.day_of_week) % 7]))).filter(Boolean);
  const first = schedules[0];
  const time = first?.start_time && first?.end_time ? `${String(first.start_time).slice(0, 5)} - ${String(first.end_time).slice(0, 5)}` : '';
  return `${days.join(', ')}${time ? ` | ${time}` : ''}`;
}

function initials(name) {
  const parts = String(name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase());
  return parts.join('') || 'B';
}

function TeacherBatchCard({ batch: b, navigate }) {
  const preview = b.enrollmentPreview || [];
  const more = Math.max(0, (b.studentCount || 0) - preview.length);
  return (
    <Grid item xs={12} sm={6}>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 3,
          borderColor: 'rgba(0,0,0,0.08)',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900, letterSpacing: 0.4 }}>
              {b.subject ? String(b.subject).toUpperCase() : 'BATCH'}
            </Typography>
            <Typography fontWeight={900} sx={{ lineHeight: 1.15 }}>
              {b.name}
            </Typography>

            <Stack spacing={0.75} sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Starts:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {b.start_date || '—'}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                {formatSchedules(b.schedules)}
              </Typography>
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                NEW ENROLLMENT PREVIEW
              </Typography>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
                {preview.map((s) => (
                  <Avatar
                    key={s.student_id}
                    sx={{ width: 26, height: 26, fontSize: 11, bgcolor: 'rgba(11,57,84,0.12)', color: '#0b3954', fontWeight: 900 }}
                  >
                    {initials(s.student_name)}
                  </Avatar>
                ))}
                {more > 0 ? (
                  <Chip size="small" label={`+${more} more`} sx={{ fontWeight: 900, bgcolor: 'rgba(0,0,0,0.06)' }} />
                ) : null}
              </Stack>
            </Box>

            {(b.upcomingMakeups || []).length > 0 ? (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 900 }}>
                  UPCOMING MAKEUP
                </Typography>
                {(b.upcomingMakeups || []).slice(0, 2).map((m) => (
                  <Typography key={m.id} variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {m.session_date} · {String(m.start_time || '').slice(0, 5)}
                  </Typography>
                ))}
              </Box>
            ) : null}

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Visibility />}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 2 }}
                onClick={() => navigate(`/batches/${b.id}`)}
              >
                Open batch
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Grid>
  );
}

export default function TeacherUpcomingBatches() {
  const navigate = useNavigate();
  const [anchorDay, setAnchorDay] = useState(dayjs());

  const query = useQuery(['teacherMyBatches'], async () => {
    const res = await teacherModuleAPI.getMyBatches();
    return res.data?.data;
  });

  const data = query.data;
  const spotlights = data?.spotlights || [];

  const displayBatches = useMemo(() => data?.batches || [], [data?.batches]);

  const todayYMD = dayjs().format('YYYY-MM-DD');
  const upcomingBatchesOnly = useMemo(() => {
    const rows = (data?.batches || []).filter((b) => {
      const sd = b?.start_date ? String(b.start_date).slice(0, 10) : '';
      return sd && sd > todayYMD;
    });
    rows.sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)) || String(a.name || '').localeCompare(String(b.name || '')));
    return rows;
  }, [data?.batches, todayYMD]);

  const launchEventsInMonth = useMemo(() => {
    const launchEvents = data?.launchEvents || [];
    const start = anchorDay.startOf('month');
    const end = anchorDay.endOf('month');
    return launchEvents.filter((e) => {
      if (!e?.date) return false;
      const d = dayjs(e.date);
      if (!d.isValid()) return false;
      return (d.isAfter(start) || d.isSame(start, 'day')) && (d.isBefore(end) || d.isSame(end, 'day'));
    });
  }, [data?.launchEvents, anchorDay]);

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/teacher/home')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" fontWeight={900}>
          My Batches
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 2, px: 2.5, bgcolor: '#0b3954', '&:hover': { bgcolor: '#082a3f' } }}
          onClick={() => navigate('/batches?create=1')}
        >
          Create Class
        </Button>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {upcomingBatchesOnly.length > 0 ? (
            <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                <Typography variant="h6" fontWeight={900}>
                  Upcoming Batches
                </Typography>
                <Chip
                  size="small"
                  label={`${upcomingBatchesOnly.length} Batches`}
                  sx={{ fontWeight: 900, bgcolor: 'rgba(11,57,84,0.08)', color: '#0b3954' }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Assigned to you with a start date after today (not yet started).
              </Typography>
              <Grid container spacing={2}>
                {upcomingBatchesOnly.map((b) => (
                  <TeacherBatchCard key={b.id} batch={b} navigate={navigate} />
                ))}
              </Grid>
            </Paper>
          ) : null}

          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
              <Typography variant="h6" fontWeight={900}>
                Your batches
              </Typography>
              <Chip
                size="small"
                label={`${Math.max(0, displayBatches.length)} Batches`}
                sx={{ fontWeight: 900, bgcolor: 'rgba(11,57,84,0.08)', color: '#0b3954' }}
              />
              <Box sx={{ flex: 1 }} />
              <Stack direction="row" spacing={1} alignItems="center">
                <Search fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  (Search coming next)
                </Typography>
              </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              All batches currently assigned to you (active, to start, or in progress).
            </Typography>

            <Grid container spacing={2}>
              {displayBatches.map((b) => (
                <TeacherBatchCard key={b.id} batch={b} navigate={navigate} />
              ))}

              {displayBatches.length === 0 ? (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, borderColor: 'rgba(0,0,0,0.08)' }}>
                    <Typography>No batches assigned to you yet.</Typography>
                  </Paper>
                </Grid>
              ) : null}
            </Grid>
          </Paper>

          <Paper sx={{ p: 2, mt: 2, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={900} sx={{ mb: 1 }}>
              Student Spotlight
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Students flagged as special needs / autistic, or having important notes (across your batches).
            </Typography>

            <Stack spacing={1.25}>
              {spotlights.map((s) => (
                <Paper
                  key={`${s.batch_id}-${s.student_id}`}
                  variant="outlined"
                  sx={{ p: 1.5, borderRadius: 3, borderColor: 'rgba(0,0,0,0.08)' }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 40, height: 40, fontWeight: 900, bgcolor: 'rgba(11,57,84,0.12)', color: '#0b3954' }}>
                      {initials(s.student_name)}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={900}>{s.student_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.batch_name}
                        {s.student_type ? ` • ${String(s.student_type).replace(/_/g, ' ')}` : ''}
                      </Typography>
                      {String(s.notes || '').trim() ? (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {s.notes}
                        </Typography>
                      ) : null}
                    </Box>
                    <Button
                      variant="text"
                      sx={{ textTransform: 'none', fontWeight: 900, color: '#0b3954' }}
                      onClick={() => navigate(`/students/${s.student_id}`)}
                    >
                      View Profile
                    </Button>
                  </Stack>
                </Paper>
              ))}

              {spotlights.length === 0 ? (
                <Typography color="text.secondary">No spotlight students found.</Typography>
              ) : null}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography fontWeight={900}>{anchorDay.format('MMMM YYYY')}</Typography>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => setAnchorDay((d) => d.subtract(1, 'month'))}>
                  <ChevronLeft />
                </IconButton>
                <IconButton size="small" onClick={() => setAnchorDay((d) => d.add(1, 'month'))}>
                  <ChevronRight />
                </IconButton>
              </Stack>
            </Stack>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={anchorDay}
                onChange={(v) => v && setAnchorDay(v)}
                sx={{
                  '& .MuiPickersDay-root.Mui-selected': { bgcolor: '#0b3954' },
                  '& .MuiPickersDay-root.Mui-selected:hover': { bgcolor: '#082a3f' },
                }}
              />
            </LocalizationProvider>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1 }}>
                LAUNCH EVENTS
              </Typography>
              <Stack spacing={1}>
                {launchEventsInMonth.map((e) => (
                  <Stack key={e.batch_id} direction="row" spacing={1} alignItems="flex-start">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#0b3954', mt: 0.75 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={900}>
                        {e.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {e.date}
                        {e.time ? `, ${String(e.time).slice(0, 5)}` : ''}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
                {launchEventsInMonth.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No batch start dates in this month.
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, borderRadius: 3, mt: 2, bgcolor: 'rgba(11,57,84,0.08)' }}>
            <Typography fontWeight={900} sx={{ mb: 0.5 }}>
              Curriculum Prep
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Your syllabus for your batches is ready for review.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 900,
                borderRadius: 2,
                bgcolor: '#0b3954',
                '&:hover': { bgcolor: '#082a3f' },
              }}
              onClick={() => toastMissing('Curriculum module')}
            >
              View Curriculum
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

function toastMissing(label) {
  // lightweight local fallback without pulling toast dependency
  // eslint-disable-next-line no-alert
  alert(`${label} is not wired yet.`);
}

